import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

export const sendOtpEmail = createAsyncThunk(
  "auth/sendOtpEmail",
  async ({ email, countryCode, phone }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "/otp/sendOtpEmail",
        {},
        {
          params: {
            Email: email,
            CountryCode: countryCode,
            Phone: phone,
          },
        },
      );
      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to send OTP email";
      console.error("Send OTP error:", err);
      return rejectWithValue(message);
    }
  },
);

export const verifyOtpAndLogin = createAsyncThunk(
  "auth/verifyOtpAndLogin",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "/otp/VerifyOtp",
        {},
        {
          params: {
            Email: email,
            Otp: otp,
          },
        },
      );

      const { data: user, message } = response.data;

      return { user, message };
    } catch (err) {
      const message = err.response?.data?.message || "Invalid or expired OTP";
      return rejectWithValue(message);
    }
  },
);

export const fetchAllUsers = createAsyncThunk(
  "userState/fetchAllUsers",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const {
        searchText,
        id = 1,
        phoneDial,
        gurudwaraId,
        categoryId,
        Location,
        Sorting,
        caId,
      } = params;

      const response = await axiosClient.get(`setting/getAllProfile/${id}`, {
        params: {
          searchText,
          phoneDial,
          gurudwaraId,
          categoryId,
          Location,
          Sorting,
          caId,
        },
      });

      const fetchedUsers = response.data.Users || [];
      const totalCount = response.data.total || fetchedUsers.length;

      dispatch(
        setUsersData({
          users: fetchedUsers,
          totalCount,
        }),
      );

      dispatch(setLoadingUsers(false));
      return fetchedUsers;
    } catch (err) {
      console.error("Error fetching users:", err);
      const message = err.response?.data?.message || "Failed to fetch users";
      dispatch(setError(message));
      dispatch(setLoadingUsers(false));
      return rejectWithValue(message);
    }
  },
);

export const updateUserStatus = createAction(
  "userState/updateUserStatus",
  (userId, caIdStatus, caIdComment = "") => ({
    payload: { userId, caIdStatus, caIdComment },
  }),
);

export const fetchPendingCaid = createAsyncThunk(
  "userState/fetchPendingCaid",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClient.get("setting/PendingCaid");
      const result = response.data.Data ?? 0;

      dispatch(setPendingCaidCount(result));
      dispatch(setLoadingPendingCaid(false));
      return result;
    } catch (err) {
      console.error("Error fetching pending CAID count:", err);
      const message =
        err.response?.data?.message || "Failed to fetch pending CAID count";
      dispatch(setError(message));
      dispatch(setLoadingPendingCaid(false));
      return rejectWithValue(message);
    }
  },
);

const initialState = {
  finalUsers: [],
  loadingUsers: false,
  loadingPendingCaid: false,
  extRequestFilter: false,
  selectedGurdwara: 0,
  pendingCaidCount: 0,
  usersCount: 0,
  currentUsersPage: 1,
  searchUserQuery: "",
  error: null,
  userSearchText: "",
  sortBy: "",
};

const userDataSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
    setUsersData: (state, action) => {
      state.finalUsers = action.payload.users;
      state.usersCount = action.payload.totalCount;
    },
    setLoadingUsers: (state, action) => {
      state.loadingUsers = action.payload;
    },
    setLoadingPendingCaid: (state, action) => {
      state.loadingPendingCaid = action.payload;
    },
    setExtRequestFilter: (state, action) => {
      state.extRequestFilter = action.payload;
    },
    setSelectedGurdwara: (state, action) => {
      state.selectedGurdwara = action.payload;
    },
    setPendingCaidCount: (state, action) => {
      state.pendingCaidCount = action.payload;
    },
    setUsersCount: (state, action) => {
      state.usersCount = action.payload;
    },
    setCurrentUsersPage: (state, action) => {
      state.currentUsersPage = action.payload;
    },
    setSearchUserQuery: (state, action) => {
      state.searchUserQuery = action.payload;
    },
    setUserSearchText: (state, action) => {
      state.userSearchText = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(fetchAllUsers.pending, (state) => {
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state) => {
        state.loadingUsers = false;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loadingUsers = false;
        state.error = action.payload;
      })

      .addCase(fetchPendingCaid.pending, (state) => {
        state.loadingPendingCaid = true;
        state.error = null;
      })
      .addCase(fetchPendingCaid.fulfilled, (state) => {
        state.loadingPendingCaid = false;
      })
      .addCase(fetchPendingCaid.rejected, (state, action) => {
        state.loadingPendingCaid = false;
        state.error = action.payload;
      })

      .addCase(updateUserStatus, (state, action) => {
        const { userId, caIdStatus, caIdComment } = action.payload;
        state.finalUsers = state.finalUsers.map((user) =>
          user.Id === userId ? { ...user, caIdStatus, caIdComment } : user,
        );
      });
  },
});

export const {
  setUsersData,
  setLoadingUsers,
  setLoadingPendingCaid,
  setExtRequestFilter,
  setSelectedGurdwara,
  setPendingCaidCount,
  setUsersCount,
  setCurrentUsersPage,
  setSearchUserQuery,
  setUserSearchText,
  setSortBy,
  setError,
  clearError,
} = userDataSlice.actions;

export default userDataSlice.reducer;
